/**
 * 6.4 配置驱动属性面板（借鉴 Appsmith）
 *
 * PropertyPaneConfig 类型定义于 shared/types（前后端共享），本文件提供
 * 从 propSchema 到 PropertyPaneConfig 的转换辅助函数，以及面板渲染所需的工具：
 * - convertPropSchemaToControls：PropType -> ControlType 映射
 * - buildDefaultPropertyPaneConfig：propSchema + "动效" section 的默认配置
 * - flattenPropertyPaneControls / filterPropertyPaneConfig：供属性面板渲染与测试
 *
 * 动效字段 motionPreset 的 DROPDOWN 选项与动效引擎 MOTION_PRESETS 对齐
 * （单一来源：lib/builder/motion/presets.ts，值 none/fade-in/slide-up/scale-in；
 * 引擎扩展新预设后此处自动同步）。
 */
import type {
  ControlType,
  PropSchema,
  PropertyPaneConfig,
  PropertyPaneControl,
} from "@shared/types";

import { MOTION_PRESET_OPTIONS } from "../motion/presets";

export { MOTION_PRESET_OPTIONS } from "../motion/presets";

export const MOTION_PRESET_DEFAULT = "none";

/** 默认"动效" section（6.4 示例） */
export function buildMotionSection(): PropertyPaneConfig {
  return {
    sectionName: "动效",
    children: [
      {
        propertyName: "motionPreset",
        label: "动效预设",
        controlType: "DROPDOWN",
        isBindProperty: true,
        options: [...MOTION_PRESET_OPTIONS],
        defaultValue: MOTION_PRESET_DEFAULT,
        helpText: "进场动效预设（由动效引擎执行）",
      },
    ],
  };
}

/**
 * 由事件 schema 生成"事件" section（EVENT_BINDER 控件）。
 * 配合 action 触发器绑定 `{{ showAlert('ok'); saveCustomer(); }}` 使用。
 */
export function buildEventBinderSection(
  events: { event: string; label: string }[],
): PropertyPaneConfig {
  return {
    sectionName: "事件",
    children: events.map((e) => ({
      propertyName: e.event,
      label: e.label,
      controlType: "EVENT_BINDER",
      isTriggerProperty: true,
      isBindProperty: true,
      helpText: "配置动作序列或绑定（如 {{ showAlert('保存成功', 'info'); saveCustomer(); }}）",
    })),
  };
}

/** 组装物料的默认 propertyPaneConfig：主 section + 动效 section + 自定义 section */
export function buildMaterialPropertyPaneConfig(
  propSchema: PropSchema[],
  extraSections: PropertyPaneConfig[] = [],
): PropertyPaneConfig[] {
  return [...buildDefaultPropertyPaneConfig(propSchema), ...extraSections];
}

/** PropType -> ControlType 映射 */
export function controlTypeFromPropType(type: PropSchema["type"]): ControlType {
  switch (type) {
    case "number":
      return "INPUT_NUMBER";
    case "boolean":
      return "SWITCH";
    case "select":
      return "DROPDOWN";
    case "color":
      return "COLOR_PICKER";
    case "json":
      return "JSON_INPUT";
    case "dataBinding":
      return "DATA_BINDING";
    case "textarea":
      return "TEXT_AREA";
    default:
      return "INPUT_TEXT";
  }
}

/** ControlType -> PropType（面板写入值类型与 propSchema 对齐） */
export function propTypeFromControlType(controlType: ControlType): PropSchema["type"] {
  switch (controlType) {
    case "INPUT_NUMBER":
      return "number";
    case "SWITCH":
      return "boolean";
    case "DROPDOWN":
      return "select";
    case "COLOR_PICKER":
      return "color";
    case "JSON_INPUT":
      return "json";
    case "DATA_BINDING":
      return "dataBinding";
    case "TEXT_AREA":
      return "textarea";
    default:
      return "text";
  }
}

/** 单个 propSchema 字段 -> PropertyPaneControl */
export function convertPropSchemaToControl(field: PropSchema): PropertyPaneControl {
  const control: PropertyPaneControl = {
    propertyName: field.name,
    label: field.label,
    controlType: controlTypeFromPropType(field.type),
    isBindProperty: field.type === "dataBinding" || field.type === "text" || field.type === "textarea",
    defaultValue: field.defaultValue,
  };
  if (field.options) control.options = field.options;
  if (field.placeholder) control.placeholder = field.placeholder;
  if (field.helpText) control.helpText = field.helpText;
  if (field.required) {
    control.validation = { type: "required" };
  }
  return control;
}

/**
 * 从 propSchema 生成默认属性面板配置：
 * - 主 section（"内容"）：全部 propSchema 控件
 * - "动效" section：motionPreset DROPDOWN（6.4 示例）
 */
export function buildDefaultPropertyPaneConfig(
  propSchema: PropSchema[],
  options: { mainSectionName?: string; includeMotionSection?: boolean } = {},
): PropertyPaneConfig[] {
  const { mainSectionName = "内容", includeMotionSection = true } = options;
  const config: PropertyPaneConfig[] = [
    {
      sectionName: mainSectionName,
      children: propSchema.map(convertPropSchemaToControl),
    },
  ];
  if (includeMotionSection) {
    config.push(buildMotionSection());
  }
  return config;
}

/** 展平所有 section 的控件列表 */
export function flattenPropertyPaneControls(config: PropertyPaneConfig[] | undefined): PropertyPaneControl[] {
  return (config ?? []).flatMap((section) => section.children);
}

/** 按关键词过滤配置（匹配 sectionName / label / propertyName） */
export function filterPropertyPaneConfig(
  config: PropertyPaneConfig[] | undefined,
  term: string,
): PropertyPaneConfig[] {
  if (!config) return [];
  const keyword = term.trim().toLowerCase();
  if (!keyword) return config;
  return config
    .map((section) => ({
      ...section,
      children: section.children.filter(
        (c) =>
          c.label.toLowerCase().includes(keyword) ||
          c.propertyName.toLowerCase().includes(keyword) ||
          section.sectionName.toLowerCase().includes(keyword),
      ),
    }))
    .filter((section) => section.children.length > 0);
}

/** 查找指定 propertyName 的控件 */
export function findControl(
  config: PropertyPaneConfig[] | undefined,
  propertyName: string,
): PropertyPaneControl | undefined {
  return flattenPropertyPaneControls(config).find((c) => c.propertyName === propertyName);
}

/** 配置中是否包含事件触发器控件（EVENT_BINDER） */
export function hasEventBinder(config: PropertyPaneConfig[] | undefined): boolean {
  return flattenPropertyPaneControls(config).some(
    (c) => c.controlType === "EVENT_BINDER" || c.isTriggerProperty,
  );
}
